import styled from '@emotion/styled';
import { Autocomplete, Chip, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from '@mui/material';
import * as enabledApi from '@shared/api/enabledPlayApi';
import Button from '@shared/components/core/button/CoreButton';
import { ButtonRow, CardBorder, WrapCardText } from '@shared/components/core/card/CommonCardStyles';
import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import { Switch } from '@shared/components/core/field/CommonFieldStyles';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import Icons from '@shared/components/core/images/Icons';
import SearchField from '@shared/components/core/search/SearchField';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import { BodyMedium, BodySmall, TitleSmall, TitleXSmall } from '@shared/components/core/text/Typography';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import AutomationRuleModel from '@shared/models/organizations/AutomationRuleModel';
import { AutomationRuleType } from '@shared/models/organizations/AutomationRuleType';
import CreateAutomationRuleRequest from '@shared/models/organizations/CreateAutomationRuleRequest';
import OrganizationMemberModel from '@shared/models/organizations/OrganizationMemberModel';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IAutomationRulesManagerProps {
    organizationId: string;
}

type IRuleFormValues = CreateAutomationRuleRequest;

const emptyRule: IRuleFormValues = {
    applyToAllMembers: true,
    ruleType: 'Block',
    appOrDomainName: '',
    rulePrompt: '',
};

function getRuleValues(rule?: AutomationRuleModel | null): IRuleFormValues {
    if (!rule) return emptyRule;
    return {
        applyToAllMembers: rule.applyToAllMembers,
        ruleType: rule.ruleType,
        appOrDomainName: rule.appOrDomainName ?? '',
        rulePrompt: rule.rulePrompt ?? '',
    };
}

export default function AutomationRulesManager({ organizationId }: Readonly<IAutomationRulesManagerProps>) {
    const { t } = useTranslation();
    const [rules, setRules] = useState<AutomationRuleModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editingRule, setEditingRule] = useState<AutomationRuleModel | null | undefined>(undefined);
    const [deletingRule, setDeletingRule] = useState<AutomationRuleModel | null>(null);
    const [assigningRule, setAssigningRule] = useState<AutomationRuleModel | null>(null);

    const loadRules = async () => {
        setIsLoading(true);
        try {
            const result = await enabledApi.getOrganizationAutomationRules(organizationId);
            if (result.resultType !== 'Ok' || !result.data) throw new Error('Unable to load automation rules');
            setRules(result.data);
        } catch (error) {
            useAlertsStore.getState().showError(t('common:organization.governance.automation.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadRules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const filteredRules = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) return rules;
        return rules.filter((rule) =>
            [
                rule.appOrDomainName,
                rule.rulePrompt,
                rule.ruleType,
                rule.applyToAllMembers ? 'organization' : 'member',
            ].some((value) => value?.toLowerCase().includes(normalizedSearch))
        );
    }, [rules, search]);

    const saveRule = async (values: IRuleFormValues) => {
        try {
            const result = editingRule
                ? await enabledApi.updateAutomationRule(organizationId, editingRule.id, values)
                : await enabledApi.createAutomationRule(organizationId, values);
            if (result.resultType !== 'Ok' || !result.data) throw new Error('Unable to save automation rule');
            setEditingRule(undefined);
            await loadRules();
            useAlertsStore
                .getState()
                .showSuccess(
                    t(
                        editingRule
                            ? 'common:organization.governance.automation.updated'
                            : 'common:organization.governance.automation.created'
                    )
                );
        } catch (error) {
            useAlertsStore.getState().showError(t('common:organization.governance.automation.saveError'));
        }
    };

    const deleteRule = async () => {
        if (!deletingRule) return;
        try {
            const result = await enabledApi.deleteAutomationRule(organizationId, deletingRule.id);
            if (result.resultType !== 'Ok') throw new Error('Unable to delete automation rule');
            setDeletingRule(null);
            await loadRules();
            useAlertsStore.getState().showSuccess(t('common:organization.governance.automation.deleted'));
        } catch (error) {
            useAlertsStore.getState().showError(t('common:organization.governance.automation.deleteError'));
        }
    };

    return (
        <ManagerWrap>
            <GovernanceIntro>
                <IntroIcon>
                    <Icons name="automation" size={30} />
                </IntroIcon>
                <div>
                    <TitleSmall>{t('common:organization.governance.automation.title')}</TitleSmall>
                    <BodyMedium>{t('common:organization.governance.automation.description')}</BodyMedium>
                </div>
            </GovernanceIntro>

            <GuidanceGrid>
                <GuidanceCard>
                    <TitleXSmall>{t('common:organization.governance.automation.guidance.block.title')}</TitleXSmall>
                    <BodySmall>{t('common:organization.governance.automation.guidance.block.description')}</BodySmall>
                </GuidanceCard>
                <GuidanceCard>
                    <TitleXSmall>{t('common:organization.governance.automation.guidance.prompt.title')}</TitleXSmall>
                    <BodySmall>{t('common:organization.governance.automation.guidance.prompt.description')}</BodySmall>
                </GuidanceCard>
                <GuidanceCard>
                    <TitleXSmall>{t('common:organization.governance.automation.guidance.scope.title')}</TitleXSmall>
                    <BodySmall>{t('common:organization.governance.automation.guidance.scope.description')}</BodySmall>
                </GuidanceCard>
            </GuidanceGrid>

            <SubSectionRow title={t('common:organization.governance.automation.rulesTitle')} variant="small">
                <Button $color="secondary" $size="small" startIcon="plus" onClick={() => setEditingRule(null)}>
                    {t('common:organization.governance.automation.create')}
                </Button>
            </SubSectionRow>

            <SearchField
                label=""
                placeholder={t('common:organization.governance.automation.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onClear={() => setSearch('')}
            />

            {isLoading ? (
                <SpacedLoader />
            ) : filteredRules.length ? (
                <RuleList>
                    {filteredRules.map((rule) => (
                        <RuleCard key={rule.id}>
                            <WrapCardText>
                                <RuleHeading>
                                    <RuleIdentity>
                                        <Chip
                                            label={t(
                                                `common:organization.governance.automation.type.${rule.ruleType.toLowerCase()}`
                                            )}
                                            color={rule.ruleType === 'Block' ? 'error' : 'primary'}
                                            size="small"
                                        />
                                        <TitleSmall>
                                            {rule.appOrDomainName ||
                                                t('common:organization.governance.automation.promptOnly')}
                                        </TitleSmall>
                                    </RuleIdentity>
                                    <Chip
                                        label={t(
                                            rule.applyToAllMembers
                                                ? 'common:organization.governance.automation.scope.organization'
                                                : 'common:organization.governance.automation.scope.members'
                                        )}
                                        variant="outlined"
                                        size="small"
                                    />
                                </RuleHeading>
                                {rule.rulePrompt && <RulePrompt>{rule.rulePrompt}</RulePrompt>}
                                <ButtonRow>
                                    <Button
                                        $color="text"
                                        $size="small"
                                        startIcon="edit"
                                        onClick={() => setEditingRule(rule)}>
                                        {t('common:edit')}
                                    </Button>
                                    {!rule.applyToAllMembers && (
                                        <Button
                                            $color="text"
                                            $size="small"
                                            startIcon="user"
                                            onClick={() => setAssigningRule(rule)}>
                                            {t('common:organization.governance.automation.assign')}
                                        </Button>
                                    )}
                                    <Button
                                        $color="negative"
                                        $size="small"
                                        startIcon="cancel2"
                                        onClick={() => setDeletingRule(rule)}>
                                        {t('common:delete')}
                                    </Button>
                                </ButtonRow>
                            </WrapCardText>
                        </RuleCard>
                    ))}
                </RuleList>
            ) : (
                <EmptyState>
                    <Icons name="automation" size={32} />
                    <TitleSmall>{t('common:organization.governance.automation.empty.title')}</TitleSmall>
                    <BodyMedium>{t('common:organization.governance.automation.empty.description')}</BodyMedium>
                </EmptyState>
            )}

            {editingRule !== undefined && (
                <AutomationRuleDialog rule={editingRule} onClose={() => setEditingRule(undefined)} onSave={saveRule} />
            )}
            <ContentDialog
                open={!!deletingRule}
                title={t('common:organization.governance.automation.delete.title')}
                description={t('common:organization.governance.automation.delete.description')}
                confirmButtonText={t('common:delete')}
                confirmIcon="cancel2"
                onConfirm={deleteRule}
                onClose={() => setDeletingRule(null)}
                onCancel={() => setDeletingRule(null)}
            />
            {assigningRule && (
                <MemberAssignmentDialog
                    organizationId={organizationId}
                    rule={assigningRule}
                    onClose={() => setAssigningRule(null)}
                />
            )}
        </ManagerWrap>
    );
}

function AutomationRuleDialog({
    rule,
    onClose,
    onSave,
}: Readonly<{
    rule: AutomationRuleModel | null;
    onClose: () => void;
    onSave: (values: IRuleFormValues) => Promise<void>;
}>) {
    const { t } = useTranslation();
    const [values, setValues] = useState<IRuleFormValues>(() => getRuleValues(rule));
    const [showValidation, setShowValidation] = useState(false);
    const isValid = Boolean(values.appOrDomainName.trim() || values.rulePrompt.trim());

    const save = async () => {
        if (!isValid) {
            setShowValidation(true);
            return;
        }
        await onSave({
            ...values,
            appOrDomainName: values.appOrDomainName.trim(),
            rulePrompt: values.rulePrompt.trim(),
        });
    };

    return (
        <ContentDialog
            fullWidth
            maxWidth="sm"
            open={true}
            title={t(
                rule
                    ? 'common:organization.governance.automation.edit'
                    : 'common:organization.governance.automation.create'
            )}
            description={t('common:organization.governance.automation.editorDescription')}
            confirmButtonText={t('common:save')}
            onConfirm={save}
            onClose={onClose}
            onCancel={onClose}>
            <DialogForm>
                <FormControl fullWidth size="small">
                    <InputLabel id="automation-rule-type-label">
                        {t('common:organization.governance.automation.type.label')}
                    </InputLabel>
                    <Select
                        labelId="automation-rule-type-label"
                        label={t('common:organization.governance.automation.type.label')}
                        value={values.ruleType}
                        onChange={(event) =>
                            setValues((current) => ({ ...current, ruleType: event.target.value as AutomationRuleType }))
                        }>
                        <MenuItem value="Block">{t('common:organization.governance.automation.type.block')}</MenuItem>
                        <MenuItem value="Allow">{t('common:organization.governance.automation.type.allow')}</MenuItem>
                    </Select>
                </FormControl>
                <StyledTextField
                    label={t('common:organization.governance.automation.appOrDomain.label')}
                    helperText={t('common:organization.governance.automation.appOrDomain.helper')}
                    value={values.appOrDomainName}
                    onChange={(event) => setValues((current) => ({ ...current, appOrDomainName: event.target.value }))}
                    fullWidth
                />
                <StyledTextField
                    label={t('common:organization.governance.automation.prompt.label')}
                    helperText={
                        showValidation && !isValid
                            ? t('common:organization.governance.automation.validation')
                            : t('common:organization.governance.automation.prompt.helper')
                    }
                    error={showValidation && !isValid}
                    value={values.rulePrompt}
                    onChange={(event) => setValues((current) => ({ ...current, rulePrompt: event.target.value }))}
                    multiline
                    minRows={4}
                    fullWidth
                />
                <ScopeSwitch
                    control={
                        <Switch
                            checked={values.applyToAllMembers}
                            onChange={(event) =>
                                setValues((current) => ({ ...current, applyToAllMembers: event.target.checked }))
                            }
                        />
                    }
                    label={
                        <div>
                            <TitleXSmall>
                                {t('common:organization.governance.automation.scope.organization')}
                            </TitleXSmall>
                            <BodySmall>
                                {t('common:organization.governance.automation.scope.organizationDescription')}
                            </BodySmall>
                        </div>
                    }
                />
                {!values.applyToAllMembers && (
                    <ScopeNotice>{t('common:organization.governance.automation.scope.membersDescription')}</ScopeNotice>
                )}
            </DialogForm>
        </ContentDialog>
    );
}

function MemberAssignmentDialog({
    organizationId,
    rule,
    onClose,
}: Readonly<{ organizationId: string; rule: AutomationRuleModel; onClose: () => void }>) {
    const { t } = useTranslation();
    const [members, setMembers] = useState<OrganizationMemberModel[]>([]);
    const [selectedMember, setSelectedMember] = useState<OrganizationMemberModel | null>(null);
    const [isApplied, setIsApplied] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const loadMembers = async () => {
            try {
                const result = await enabledApi.getOrganizationMembers(organizationId, { take: 100, skip: 0 });
                if (result.resultType !== 'Ok' || !result.data) throw new Error('Unable to load organization members');
                setMembers(result.data);
            } catch (error) {
                useAlertsStore
                    .getState()
                    .showError(t('common:organization.governance.automation.assignment.loadError'));
            } finally {
                setIsLoading(false);
            }
        };
        loadMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const selectMember = async (_: unknown, member: OrganizationMemberModel | null) => {
        setSelectedMember(member);
        setIsApplied(null);
        if (!member) return;
        try {
            const result = await enabledApi.getAutomationRulesForSpecificMember(organizationId, member.id);
            if (result.resultType !== 'Ok' || !result.data) throw new Error('Unable to load member rules');
            setIsApplied(result.data.some((memberRule) => memberRule.id === rule.id));
        } catch (error) {
            useAlertsStore.getState().showError(t('common:organization.governance.automation.assignment.statusError'));
        }
    };

    const updateAssignment = async () => {
        if (!selectedMember || isApplied === null) return;
        setIsSaving(true);
        try {
            const result = isApplied
                ? await enabledApi.removeAutomationRuleFromMember(organizationId, selectedMember.id, rule.id)
                : await enabledApi.applyAutomationRuleToMember(organizationId, selectedMember.id, rule.id);
            if (result.resultType !== 'Ok') throw new Error('Unable to update rule assignment');
            setIsApplied(!isApplied);
            useAlertsStore
                .getState()
                .showSuccess(
                    t(
                        isApplied
                            ? 'common:organization.governance.automation.assignment.removed'
                            : 'common:organization.governance.automation.assignment.applied'
                    )
                );
        } catch (error) {
            useAlertsStore.getState().showError(t('common:organization.governance.automation.assignment.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ContentDialog
            fullWidth
            maxWidth="sm"
            open={true}
            title={t('common:organization.governance.automation.assignment.title')}
            description={t('common:organization.governance.automation.assignment.description', {
                value: rule.appOrDomainName || t('common:organization.governance.automation.promptOnly'),
            })}
            confirmButtonText={
                isApplied
                    ? t('common:organization.governance.automation.assignment.remove')
                    : t('common:organization.governance.automation.assignment.apply')
            }
            onConfirm={updateAssignment}
            onClose={onClose}
            onCancel={onClose}>
            {isLoading ? (
                <SpacedLoader />
            ) : (
                <DialogForm>
                    <Autocomplete
                        options={members}
                        value={selectedMember}
                        onChange={selectMember}
                        getOptionLabel={(member) => member.user?.email ?? ''}
                        renderInput={(params) => (
                            <StyledTextField
                                {...params}
                                label={t('common:organization.governance.automation.assignment.memberLabel')}
                            />
                        )}
                    />
                    {selectedMember && isApplied !== null && (
                        <AssignmentStatus $applied={isApplied}>
                            {t(
                                isApplied
                                    ? 'common:organization.governance.automation.assignment.assigned'
                                    : 'common:organization.governance.automation.assignment.notAssigned'
                            )}
                        </AssignmentStatus>
                    )}
                    {isSaving && <BodySmall>{t('common:loading')}</BodySmall>}
                </DialogForm>
            )}
        </ContentDialog>
    );
}

const ManagerWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    margin-top: 1.5rem;
`;

const GovernanceIntro = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
    border: 1px solid ${(props) => props.theme.color.border};
    border-radius: 0.5rem;
    background: ${(props) => props.theme.color.accentCard};
`;

const IntroIcon = styled.div`
    color: ${(props) => props.theme.color.accent};
`;

const GuidanceGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: 0.75rem;
`;

const GuidanceCard = styled(CardBorder)`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 1rem;
`;

const RuleList = styled.div`
    display: flex;
    flex-direction: column;
`;

const RuleCard = styled(CardBorder)`
    &:not(:last-child) {
        margin-bottom: 0.75rem;
    }
`;

const RuleHeading = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
`;

const RuleIdentity = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const RulePrompt = styled(BodyMedium)`
    white-space: pre-wrap;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 2.5rem 1rem;
    text-align: center;
    color: ${(props) => props.theme.color.text};
    border: 1px dashed ${(props) => props.theme.color.border};
    border-radius: 0.5rem;
`;

const DialogForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
`;

const ScopeSwitch = styled(FormControlLabel)`
    align-items: flex-start;
    margin: 0;
`;

const ScopeNotice = styled(BodySmall)`
    padding: 0.75rem;
    border-left: 3px solid ${(props) => props.theme.color.accent};
    background: ${(props) => props.theme.color.accentCard};
`;

const AssignmentStatus = styled(BodySmall)<{ $applied: boolean }>`
    color: ${({ $applied, theme }) => ($applied ? theme.color.success : theme.color.text)};
`;
